import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import type Theme from 'core/themes/model'
import TextField from './TextField'

interface Props {
  id?: string
  onChange: (value: number) => void
  value?: number
  className?: string
  disabled?: boolean
  iconSize?: 'sm' | 'lg'
  min?: number
  max?: number
  allowTypedInput?: boolean
  // Reject decimals and negatives at input time (keys, paste, and typed values).
  wholeNumbersOnly?: boolean
  required?: boolean
  validations?: any[]
}

export default function QuantitySelector({
  id,
  value,
  onChange,
  className,
  disabled = false,
  iconSize = 'sm',
  min = 0,
  max,
  allowTypedInput = true,
  wholeNumbersOnly = false,
  required = false,
  validations = [],
}: Props) {
  const classes = useStyles()
  const [inputValue, setInputValue] = React.useState(value)

  // A disabled selector is driven purely by the `value` prop so it can never drift from it.
  const displayValue = disabled ? value : inputValue

  const decrement = () => {
    if (disabled) return
    const current = Number(inputValue) || 0
    const newValue = current - 1 < min ? current : current - 1
    onChange(newValue)
    setInputValue(newValue)
  }

  const increment = () => {
    if (disabled) return
    const current = Number(inputValue) || 0
    const newValue = max && current + 1 > max ? max : current + 1
    onChange(newValue)
    setInputValue(newValue)
  }

  const handleInputChange = (newValue) => {
    if (disabled) return
    if (
      wholeNumbersOnly &&
      newValue !== '' &&
      (!Number.isInteger(Number(newValue)) || Number(newValue) < 0)
    ) {
      return
    }
    setInputValue(newValue)
    onChange(newValue)
  }

  const blockedKeys = ['.', ',', '-', '+', 'e', 'E']
  const wholeNumberInputProps = wholeNumbersOnly
    ? {
        inputMode: 'numeric' as const,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (blockedKeys.includes(e.key)) e.preventDefault()
        },
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
          if (!/^\d+$/.test(e.clipboardData.getData('text').trim())) e.preventDefault()
        },
      }
    : {}

  return (
    <div
      className={clsx(
        classes.quantitySelector,
        disabled && classes.quantitySelectorDisabled,
        className,
      )}
      data-testid="quantity-selector"
    >
      <button
        type="button"
        className={clsx(classes.button, disabled && classes.buttonDisabled, 'button')}
        onClick={decrement}
        disabled={disabled}
        data-testid="decrement-btn"
      >
        <FontAwesomeIcon
          size={iconSize}
          className={clsx(classes.icon, disabled && classes.iconDisabled)}
        >
          minus
        </FontAwesomeIcon>
      </button>
      <TextField
        id={id}
        data-testid="quantity-input-field"
        type="number"
        value={displayValue}
        className={classes.input}
        onChange={handleInputChange}
        min={min}
        disabled={disabled}
        readOnly={disabled}
        aria-disabled={disabled}
        {...wholeNumberInputProps}
        required={required}
        validations={validations}
        errorClassName={classes.error}
        nonce={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
        enterKeyHint={undefined}
      />
      <button
        type="button"
        className={clsx(classes.button, disabled && classes.buttonDisabled, 'button')}
        onClick={increment}
        disabled={disabled}
        data-testid="increment-btn"
      >
        <FontAwesomeIcon
          size={iconSize}
          className={clsx(classes.icon, disabled && classes.iconDisabled)}
        >
          plus
        </FontAwesomeIcon>
      </button>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  quantitySelector: {
    display: 'grid',
    gridTemplateColumns: '30% 40% 30%',
    height: 'min-content',
    width: '127px',
    border: `1px solid ${theme.palette.grey[200]}`,
    transition: 'all .2s ease',
    backgroundColor: theme.palette.common.white,
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative',
  },
  quantitySelectorDisabled: {
    backgroundColor: theme.palette.grey[50],
    cursor: 'not-allowed',
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.components.selectableCard.activeBackground,
    },
    borderRadius: '4px',
  },
  buttonDisabled: {
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  input: {
    minWidth: '32px',
    maxWidth: '70px',
    gap: '0px',
    '& .inputFrame': {
      borderTop: 'none',
      borderBottom: 'none',
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
      borderRight: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: 0,
    },
    '& .input': {
      textAlign: 'center',
      '-moz-appearance': 'textfield', // hide increment/decrement arrows in Firefox
    },
    // Hide the up and down arrows in all browsers
    '& .input::-webkit-inner-spin-button, & .input::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& .input::-ms-clear': {
      display: 'none',
    },
  },
  // Show validation errors under the whole stepper instead of squeezed inside the number input.
  error: {
    '&&': {
      position: 'absolute',
      top: '100%',
      left: 0,
      margin: '4px 0 0',
      whiteSpace: 'nowrap',
    },
  },
  icon: {
    color: theme.palette.grey[900],
  },
  iconDisabled: {
    color: theme.palette.grey[300],
  },
}))
